import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SocialSidebar from '../components/layout/SocialSidebar';
import { leasingLoansProducts, companyInfo } from '../data/mockData';
import { useLanguage } from '../contexts/LanguageContext';
import { Car, Wallet, Building2, Home, GraduationCap, FileText, ArrowRight, Phone, CheckCircle } from 'lucide-react';

const iconMap = {
  Car, Wallet, Building2, Home, GraduationCap, FileText
};

const LeasingLoansPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Leasing & Loans - {companyInfo.name}</title>
        <meta name="description" content="Explore our comprehensive leasing and loan products including Auto Loan, Leasing, Personal Loan, Business Loan, Mortgage Loan, and Education Loan." />
        <meta name="keywords" content="auto loan, car leasing, personal loan, business loan, mortgage, education loan, Sri Lanka" />
      </Helmet>

      <Header />
      <SocialSidebar />

      <main>
        {/* Hero Section - Full Height */}
        <div className="relative h-[100svh] min-h-[550px] md:min-h-[650px]">
          <img
            src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=1920&h=1080&fit=crop&q=100"
            alt="Leasing and Loans"
            className="w-full h-full object-cover brightness-125 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/15 via-transparent to-black/10" />
          <div className="absolute inset-0 flex items-center pt-[100px]">
            <div className="max-w-7xl mx-auto px-4 w-full">
              <nav className="text-white/70 text-sm mb-4">
                <Link to="/" className="hover:text-white">{t('nav.home')}</Link>
                <span className="mx-2">/</span>
                <Link to="/products" className="hover:text-white">{t('nav.products')}</Link>
                <span className="mx-2">/</span>
                <span className="text-white">{t('productsDropdown.leasingLoans')}</span>
              </nav>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4" style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.7)' }}>
                {t('pages.leasingLoansTitle')}
              </h1>
              <p className="text-white text-lg md:text-xl max-w-2xl mb-6 font-medium" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.6)' }}>
                {t('pages.leasingLoansSubtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#products"
                  className="bg-white text-[#2A9D8F] px-6 py-3 rounded font-semibold hover:bg-gray-100 transition-colors"
                >
                  Explore Products
                </a>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="border-2 border-white text-white px-6 py-3 rounded font-semibold hover:bg-white hover:text-[#2A9D8F] transition-colors flex items-center"
                >
                  <Phone size={18} className="mr-2" />
                  Call Us Now
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-white shadow-md">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-[#2A9D8F]">50M+</p>
                <p className="text-gray-500 text-sm">Loans Disbursed</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#2A9D8F]">24hrs</p>
                <p className="text-gray-500 text-sm">Quick Approval</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#2A9D8F]">12%</p>
                <p className="text-gray-500 text-sm">Starting Rate</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#2A9D8F]">5K+</p>
                <p className="text-gray-500 text-sm">Happy Customers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <section id="products" className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Leasing & Loan Products
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Choose from our wide range of financing options designed to meet your specific needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {leasingLoansProducts.map((product) => {
                const Icon = iconMap[product.icon] || Car;
                return (
                  <Link
                    key={product.slug}
                    to={`/products/leasing-loans/${product.slug}`}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 group"
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center text-white">
                          <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg mr-3">
                            <Icon size={24} />
                          </div>
                          <h3 className="text-xl font-bold">{product.title}</h3>
                        </div>
                      </div>
                      {product.hasCalculator && (
                        <span className="absolute top-3 right-3 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                          EMI Calculator
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <p className="text-gray-600 text-sm mb-4">
                        {product.shortDescription}
                      </p>

                      {/* Key features */}
                      <div className="space-y-2 mb-4">
                        {product.features.slice(0, 3).map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-500">
                            <CheckCircle size={14} className="text-green-500 mr-2 flex-shrink-0" />
                            <span className="line-clamp-1">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* CTA */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-[#2A9D8F] font-semibold flex items-center group-hover:underline">
                          Learn More
                          <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                        </span>
                        <span className="text-xs text-gray-400">
                          Up to {product.interestRates?.[0]?.maxTenure || '7 years'}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-[#2A9D8F]">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              {t('sections.whyChooseUs')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: t('sections.quickApproval'), desc: t('sections.quickApprovalDesc') },
                { title: t('sections.competitiveRates'), desc: t('sections.competitiveRatesDesc') },
                { title: t('sections.flexibleTerms'), desc: t('sections.flexibleTermsDesc') },
                { title: t('sections.expertSupport'), desc: t('sections.expertSupportDesc') }
              ].map((item, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                  <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-white/80 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-100">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('sections.readyToStart')}
            </h2>
            <p className="text-gray-600 mb-8">
              {t('sections.readyToStartDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="bg-[#2A9D8F] text-white px-8 py-3 rounded font-semibold hover:bg-[#0055a0] transition-colors"
              >
                Apply Now
              </Link>
              <a
                href={`tel:${companyInfo.phone}`}
                className="border-2 border-[#2A9D8F] text-[#2A9D8F] px-8 py-3 rounded font-semibold hover:bg-[#2A9D8F] hover:text-white transition-colors"
              >
                Call {companyInfo.phone}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LeasingLoansPage;
